import React from "react";
import { BRAND_COLORS } from "../../config/brandColors.js";

/**
 * Card para mostrar estadísticas generales con layout vertical y colores institucionales
 * monthlyCost representa el Monthly Budget (el Monthly Cost se implementará después)
 */
const OverallStatsCard = ({ 
  totalAccounts, 
  activeAccounts, 
  inactiveAccounts, 
  monthlyCost // Nota: Este parámetro representa el Monthly Budget, no el costo real
}) => {
  const activePercentage = totalAccounts > 0 ? (activeAccounts / totalAccounts) * 100 : 0;
  const inactivePercentage = 100 - activePercentage;

  return (
    <div 
      className="rounded-xl shadow-xl text-white p-6 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(120deg, rgba(77, 0, 204, 0.9) 0%, rgba(0, 8, 69, 0.9) 100%)',
        boxShadow: BRAND_COLORS.ui.shadowXL
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-5 rounded-full translate-y-12 -translate-x-12"></div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold">Multi-Cloud Overview</h2>
          <div className="bg-white bg-opacity-20 rounded-full p-2 backdrop-blur-sm">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
          </div>
        </div>

        {/* Layout Vertical: Izquierda datos, Derecha barras */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          {/* Columna Izquierda: Total Accounts y Monthly Budget */}
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2 drop-shadow-sm">{totalAccounts}</div>
              <div className="text-blue-100 text-sm font-medium uppercase tracking-wide">Total Accounts</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2 drop-shadow-sm">${monthlyCost.toLocaleString()}</div>
              <div className="text-blue-100 text-sm font-medium uppercase tracking-wide">Monthly Budget</div>
            </div>
          </div>

          {/* Columna Derecha: Barras de Active/Inactive */}
          <div className="space-y-6">
            {/* Active Accounts */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold">Active Accounts</span>
                <span className="text-sm font-bold">{activeAccounts}</span>
              </div>
              <div className="w-full bg-white bg-opacity-20 rounded-full h-3 backdrop-blur-sm">
                <div 
                  className="h-3 rounded-full transition-all duration-700 ease-out shadow-sm"
                  style={{ 
                    width: `${activePercentage}%`,
                    background: BRAND_COLORS.support.green
                  }}
                ></div>
              </div>
              <div className="text-right text-xs text-blue-100 mt-1">
                {activePercentage.toFixed(1)}%
              </div>
            </div>

            {/* Inactive Accounts */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold">Inactive Accounts</span>
                <span className="text-sm font-bold">{inactiveAccounts}</span>
              </div>
              <div className="w-full bg-white bg-opacity-20 rounded-full h-3 backdrop-blur-sm">
                <div 
                  className="h-3 rounded-full transition-all duration-700 ease-out shadow-sm"
                  style={{ 
                    width: `${inactivePercentage}%`,
                    background: BRAND_COLORS.support.darkOrange
                  }}
                ></div>
              </div>
              <div className="text-right text-xs text-blue-100 mt-1">
                {inactivePercentage.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* System Health - Alineado a la derecha y más compacto */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white border-opacity-20">
          <span className="text-sm text-blue-100 font-medium">System Health:</span>
          <div className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full shadow-sm"
              style={{
                backgroundColor: activePercentage > 80 
                  ? BRAND_COLORS.support.green 
                  : activePercentage > 60 
                    ? BRAND_COLORS.support.yellow 
                    : BRAND_COLORS.support.darkOrange
              }}
            ></div>
            <span className="text-sm font-bold">
              {activePercentage > 80 ? 'Excellent' : activePercentage > 60 ? 'Good' : 'Needs Attention'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallStatsCard;
